import { useEffect, useMemo, useRef, useState } from "react";
import type {
  ProspectListDetail,
  ProspectListViewSettings,
} from "../../../api/client.ts";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue.ts";
import { BASE_COLUMNS, type BaseColumnKey } from "../types.ts";
import { settingsEqual } from "../utils.ts";

interface UseViewSettingsPersistenceArgs {
  list: ProspectListDetail | undefined;
  onSave: (settings: ProspectListViewSettings) => void;
}

interface UseViewSettingsPersistenceResult {
  selectedLabelKeys: string[];
  setSelectedLabelKeys: React.Dispatch<React.SetStateAction<string[]>>;
  hiddenBaseColumns: BaseColumnKey[];
  setHiddenBaseColumns: React.Dispatch<React.SetStateAction<BaseColumnKey[]>>;
  sortKey: string | null;
  setSortKey: React.Dispatch<React.SetStateAction<string | null>>;
  sortDir: "asc" | "desc";
  setSortDir: React.Dispatch<React.SetStateAction<"asc" | "desc">>;
}

export function useViewSettingsPersistence({
  list,
  onSave,
}: UseViewSettingsPersistenceArgs): UseViewSettingsPersistenceResult {
  const [selectedLabelKeys, setSelectedLabelKeys] = useState<string[]>([]);
  const [hiddenBaseColumns, setHiddenBaseColumns] = useState<BaseColumnKey[]>([]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const hydratedRef = useRef<string | null>(null);
  const lastSavedRef = useRef<ProspectListViewSettings | null>(null);

  useEffect(() => {
    if (!list || hydratedRef.current === list.id) return;
    const v = list.view_settings ?? {};
    const labelColumns = Array.isArray(v.label_columns) ? v.label_columns : [];
    const hiddenBase = Array.isArray(v.hidden_base_columns)
      ? v.hidden_base_columns.filter((k): k is BaseColumnKey =>
          BASE_COLUMNS.some((c) => c.key === k),
        )
      : [];
    const nextSort =
      v.sort && typeof v.sort.key === "string"
        ? { key: v.sort.key, dir: v.sort.dir === "desc" ? ("desc" as const) : ("asc" as const) }
        : null;
    setSelectedLabelKeys(labelColumns);
    setHiddenBaseColumns(hiddenBase);
    setSortKey(nextSort?.key ?? null);
    setSortDir(nextSort?.dir ?? "asc");
    lastSavedRef.current = {
      label_columns: labelColumns,
      hidden_base_columns: hiddenBase,
      sort: nextSort,
    };
    hydratedRef.current = list.id;
  }, [list]);

  const currentSettings = useMemo<ProspectListViewSettings>(
    () => ({
      label_columns: selectedLabelKeys,
      hidden_base_columns: hiddenBaseColumns,
      sort: sortKey ? { key: sortKey, dir: sortDir } : null,
    }),
    [selectedLabelKeys, hiddenBaseColumns, sortKey, sortDir],
  );

  const debouncedSettings = useDebouncedValue(currentSettings, 600);
  useEffect(() => {
    if (!list || hydratedRef.current !== list.id) return;
    const last = lastSavedRef.current;
    if (last && settingsEqual(last, debouncedSettings)) return;
    lastSavedRef.current = debouncedSettings;
    onSave(debouncedSettings);
    // onSave is expected to be stable (mutation.mutate). Intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSettings, list]);

  return {
    selectedLabelKeys,
    setSelectedLabelKeys,
    hiddenBaseColumns,
    setHiddenBaseColumns,
    sortKey,
    setSortKey,
    sortDir,
    setSortDir,
  };
}
